import { useEffect, useState } from 'react';
import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { callApi } from 'src/utils/apiCall';

type Treatment = {
  id: number;
  name?: string;
  treatment_item?: { name?: string };
  enabled?: boolean;
  is_archived?: boolean;
};
export default function PmsTreatmentSelector({
  practiceId,
  selected,
  onSelect,
}: {
  practiceId: number | string;
  selected: Treatment | null;
  onSelect: (value: Treatment | null) => void;
}) {
  const [rows, setRows] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setRows([]);
    setLoading(true);
    setError('');
    callApi(`/api/v1/practices/${practiceId}/treatments/?enabled=true`)
      .then((response) => {
        const results = response?.data?.results ?? response?.data;
        if (!response?.ok || !Array.isArray(results)) throw new Error();
        if (active) setRows(results.filter((row) => row.enabled !== false && !row.is_archived));
      })
      .catch(() => {
        if (active) setError('Could not load treatments. Retry before selecting an appointment.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [practiceId, retry]);
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {loading && <Typography role="status">Loading treatments…</Typography>}
      {error && (
        <Alert
          severity="error"
          action={<Button onClick={() => setRetry((value) => value + 1)}>Retry treatments</Button>}
        >
          {error}
        </Alert>
      )}
      {!loading && !error && !rows.length && (
        <Alert severity="info">No enabled treatments are configured. Add a treatment before booking.</Alert>
      )}
      {!loading && !error && !!rows.length && (
        <TextField
          select
          fullWidth
          label="Appointment treatment"
          value={rows.some((row) => row.id === selected?.id) ? selected?.id : ''}
          onChange={(event) => onSelect(rows.find((row) => String(row.id) === event.target.value) || null)}
        >
          <MenuItem value="">Choose a treatment</MenuItem>
          {rows.map((row) => (
            <MenuItem key={row.id} value={String(row.id)}>
              {row.treatment_item?.name || row.name || `Treatment ${row.id}`}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}
